/**
 * @file Onglet "Munitions" de l'ecran Skins : une section par categorie d'arme (comportement), 5 skins cosmetiques chacune
 * (apercu de la balle + sa traine), achat (gemmes / Rubis) et equipement par categorie.
 */
import type { AmmoSkinDef } from '@config/types';
import { Decimal } from '@shared/bignum';
import { WEAPON_BEHAVIORS } from '@shared/enums';
import type { WalletId } from '@shared/enums';
import { Component } from '../../core/Component';
import { h, setText, toggleClass } from '../../core/dom';
import { confirmAsk, send } from '../../core/feedback';
import { ammoPreview, BEHAVIOR_ICON } from '../../core/icons';
import { uiIcon } from '../../core/uiIcons';
import { balanceOf, walletChips } from '../../core/widgets';
import type { UiContext } from '../../UiContext';

interface CardView {
  skin: AmmoSkinDef;
  root: HTMLElement;
  state: HTMLElement;
  costs: HTMLElement;
}

export class AmmoPane extends Component {
  private readonly cards: CardView[] = [];

  constructor(ctx: UiContext) {
    super(ctx, 'div', 'ammo-pane');
  }

  protected build(): void {
    const t = this.ctx.i18n;
    this.el.append(h('p', { class: 'hint' }, t.t('ammo.hint')));
    for (const behavior of WEAPON_BEHAVIORS) {
      const row = h('div', { class: 'ammo-row' });
      for (const skin of this.ctx.config.ammoSkins.filter((s) => s.behavior === behavior)) {
        const state = h('div', { class: 'skin-state' });
        const costs = h('div', { class: 'skin-costs' }, ...walletChips(this.ctx, skin.cost));
        const root = h(
          'button',
          { class: 'skin-card ammo-card', type: 'button', 'data-ui-block': true, 'data-ammo': skin.id, onclick: () => void this.activate(skin) },
          h('div', { class: 'ammo-view' }, ammoPreview(skin.style)),
          h('div', { class: 'skin-name' }, t.name(skin.name)),
          state,
          costs,
        );
        this.cards.push({ skin, root, state, costs });
        row.append(root);
      }
      this.el.append(h('section', { class: 'ammo-section' }, h('h3', { class: 'ammo-title' }, h('span', { class: 'ammo-emoji' }, uiIcon(BEHAVIOR_ICON[behavior])), t.t(`behavior.${behavior}`)), row));
    }
    this.update(0);
  }

  private async activate(skin: AmmoSkinDef): Promise<void> {
    const { data } = this.ctx.sim;
    if (data.ammoSkins.owned.includes(skin.id)) {
      if (data.ammoSkins.equipped[skin.behavior] !== skin.id) void send(this.ctx, { type: 'ammo.equip', id: skin.id });
      return;
    }
    if (skin.cost.dia !== undefined && !(await confirmAsk('skins.confirmGems'))) return;
    const r = send(this.ctx, { type: 'ammo.buy', id: skin.id });
    // Achat reussi : on l'equipe tout de suite (c'est ce que le joueur veut voir).
    if (r.ok) void send(this.ctx, { type: 'ammo.equip', id: skin.id });
  }

  override update(_dt: number): void {
    const { data } = this.ctx.sim;
    const { i18n } = this.ctx;
    for (const c of this.cards) {
      const owned = data.ammoSkins.owned.includes(c.skin.id);
      const equipped = data.ammoSkins.equipped[c.skin.behavior] === c.skin.id;
      toggleClass(c.root, 'owned', owned);
      toggleClass(c.root, 'equipped', equipped);
      toggleClass(c.costs, 'is-hidden', owned);
      setText(c.state, equipped ? i18n.t('common.equipped') : owned ? i18n.t('common.equip') : '');
      const affordable = (Object.keys(c.skin.cost) as WalletId[]).every((id) => {
        const v = c.skin.cost[id];
        return v === undefined || balanceOf(this.ctx, id).gte(Decimal.fromValue(v));
      });
      toggleClass(c.root, 'disabled', !owned && !affordable);
    }
  }
}
